import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ImageIcon from "@mui/icons-material/Image";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import api from "../../api/api";
import { clearCache } from "../../utils/cache";
import { useSnackbar } from "../../utils/snackbar";

const PAGE_SIZE = 20;
const MAX_FEATURED = 2000;
const PRODUCTS_CACHE_KEY = "admin_products_cache_v1";
const BRAND_GRAD = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

function ProductThumb({ src, title, size = 48 }) {
  if (!src) {
    return (
      <Avatar
        variant="rounded"
        sx={{ width: size, height: size, bgcolor: "action.hover" }}
      >
        <ImageIcon sx={{ color: "text.disabled", fontSize: size * 0.5 }} />
      </Avatar>
    );
  }
  return (
    <Avatar
      variant="rounded"
      src={src}
      alt={title}
      sx={{ width: size, height: size, bgcolor: "action.hover" }}
    />
  );
}

export default function AdminFeaturedProducts() {
  const theme = useTheme();
  const { showAlert } = useSnackbar();

  const [pageProducts, setPageProducts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // { [productId]: { featured: bool, featuredOrder: number | null } }
  const [selections, setSelections] = useState({});

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkFromOrder, setBulkFromOrder] = useState(1);

  // Drag-to-reorder state for active featured list
  const [featuredListOverride, setFeaturedListOverride] = useState(null);
  const [dragOverFeatId, setDragOverFeatId] = useState(null);
  const [featOrderDirty, setFeatOrderDirty] = useState(false);
  const [savingFeatOrder, setSavingFeatOrder] = useState(false);
  const draggedFeatIdRef = useRef(null);

  /* ── Load products (server-side paginated) ── */
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/api/products", {
          params: { page, limit: PAGE_SIZE, search },
        });
        if (!active) return;

        const incoming = res.data.data || [];
        setPageProducts(incoming);
        setMeta(res.data.meta);

        setSelections((prev) => {
          const next = { ...prev };
          incoming.forEach((p) => {
            if (!(p._id in next)) {
              next[p._id] = {
                featured: !!p.featured,
                featuredOrder: p.featuredOrder || null,
              };
            }
          });
          return next;
        });
      } catch {
        showAlert("Failed to load products", "error");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, search]);

  /* ── Merge server data + local selections ── */
  const paginated = useMemo(
    () =>
      pageProducts.map((p) => {
        const sel = selections[p._id];
        if (!sel) return p;
        return { ...p, featured: sel.featured, featuredOrder: sel.featuredOrder };
      }),
    [pageProducts, selections]
  );

  /* ── Featured count ── */
  const featuredCount = useMemo(
    () => Object.values(selections).filter((s) => s?.featured).length,
    [selections]
  );

  /* ── Active featured list (visible products only) ── */
  const activeFeaturedList = useMemo(() => {
    const byId = Object.fromEntries(pageProducts.map((p) => [p._id, p]));
    return Object.entries(selections)
      .filter(([, s]) => s?.featured)
      .map(([id, s]) => ({ id, product: byId[id] || null, sel: s }))
      .filter((x) => x.product)
      .sort((a, b) => (a.sel.featuredOrder ?? 9999) - (b.sel.featuredOrder ?? 9999));
  }, [selections, pageProducts]);

  // Dedicated load of ALL active featured products for the right panel (not page-filtered)
  const [allFeaturedProducts, setAllFeaturedProducts] = useState([]);
  const loadAllFeatured = useCallback(async () => {
    try {
      const res = await api.get("/api/products/all");
      const data = res.data?.data ?? [];
      const featured = data
        .filter((p) => p.featured)
        .sort((a, b) => (a.featuredOrder ?? 9999) - (b.featuredOrder ?? 9999));
      setAllFeaturedProducts(featured.map((p) => ({
        id: p._id,
        product: p,
        sel: { featured: true, featuredOrder: p.featuredOrder ?? null },
      })));
      setFeaturedListOverride(null);
    } catch {}
  }, []);

  useEffect(() => { loadAllFeatured(); }, [loadAllFeatured]);

  const totalPages = meta?.pages || 1;
  const progressPercentage = (featuredCount / MAX_FEATURED) * 100;

  /* ── Bulk selection helpers ── */
  const visibleIds = paginated.map((p) => p._id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selectedIds.includes(id));

  const handleHeaderCheckbox = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => {
        const added = visibleIds.filter((id) => !prev.includes(id));
        return [...prev, ...added];
      });
    }
  };

  const handleRowCheckbox = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ── Bulk actions ── */
  const bulkFeatureAll = () => {
    setSelections((prev) => {
      const usedOrders = new Set(
        Object.entries(prev)
          .filter(([pid, s]) => s?.featured && !selectedIds.includes(pid))
          .map(([, s]) => Number(s.featuredOrder))
          .filter(Boolean)
      );
      let nextOrder = usedOrders.size > 0 ? Math.max(...usedOrders) + 1 : 1;
      const next = { ...prev };
      for (const id of selectedIds) {
        while (usedOrders.has(nextOrder)) nextOrder++;
        usedOrders.add(nextOrder);
        next[id] = { featured: true, featuredOrder: nextOrder };
        nextOrder++;
      }
      return next;
    });
    setSelectedIds([]);
  };

  const bulkUnfeatureAll = () => {
    setSelections((prev) => {
      const next = { ...prev };
      for (const id of selectedIds) {
        next[id] = { ...next[id], featured: false, featuredOrder: null };
      }
      return next;
    });
    setSelectedIds([]);
  };

  const bulkAssignOrders = () => {
    const start = Number(bulkFromOrder);
    if (!Number.isInteger(start) || start < 1 || start > MAX_FEATURED) {
      showAlert(`"From order #" must be between 1 and ${MAX_FEATURED}.`, "error");
      return;
    }
    const featuredSelected = selectedIds.filter((id) => selections[id]?.featured);
    setSelections((prev) => {
      const next = { ...prev };
      let seq = start;
      for (const id of featuredSelected) {
        next[id] = { ...next[id], featuredOrder: seq };
        seq++;
      }
      return next;
    });
    setSelectedIds([]);
  };

  /* ── Toggle featured ── */
  const toggleFeatured = (id, checked) => {
    setSelections((prev) => {
      const usedOrders = new Set(
        Object.entries(prev)
          .filter(([pid, s]) => s?.featured && pid !== id)
          .map(([, s]) => Number(s.featuredOrder))
          .filter(Boolean)
      );
      let nextOrder = 1;
      while (usedOrders.has(nextOrder)) nextOrder++;

      if (checked && nextOrder > MAX_FEATURED) {
        showAlert(`Maximum of ${MAX_FEATURED} featured products reached.`, "error");
        return prev;
      }
      return {
        ...prev,
        [id]: { featured: checked, featuredOrder: checked ? nextOrder : null },
      };
    });
  };

  const setOrder = (id, rawOrder) => {
    const order = Number(rawOrder);
    if (!Number.isInteger(order) || order < 1 || order > MAX_FEATURED) {
      showAlert(`Featured order must be between 1 and ${MAX_FEATURED}.`, "error");
      return;
    }
    setSelections((prev) => {
      const exists = Object.entries(prev).some(
        ([pid, s]) => pid !== id && s?.featured && Number(s.featuredOrder) === order
      );
      if (exists) {
        showAlert(`Featured order ${order} is already in use.`, "error");
        return prev;
      }
      return { ...prev, [id]: { ...prev[id], featuredOrder: order } };
    });
  };

  const removeFromFeatured = (id) => toggleFeatured(id, false);

  /* ── Save display order ── */
  const saveFeaturedOrder = async () => {
    const list = featuredListOverride ?? allFeaturedProducts;
    setSavingFeatOrder(true);
    try {
      await api.put("/api/admin/products/featured/reorder", { ids: list.map((x) => x.id) });
      setFeatOrderDirty(false);
      setFeaturedListOverride(list);
      loadAllFeatured();
      showAlert("Featured order saved!", "success");
    } catch {
      showAlert("Failed to save order.", "error");
    } finally {
      setSavingFeatOrder(false);
    }
  };

  /* ── Save ── */
  const saveFeatured = async () => {
    const featured = Object.entries(selections)
      .filter(([, s]) => s?.featured)
      .map(([id, s]) => ({ id, order: Number(s.featuredOrder) }));

    if (featured.length > MAX_FEATURED) {
      showAlert(`You can only feature ${MAX_FEATURED} products.`, "error");
      return;
    }
    for (const p of featured) {
      if (!p.order || p.order < 1 || p.order > MAX_FEATURED) {
        showAlert(`Featured order must be between 1 and ${MAX_FEATURED}.`, "error");
        return;
      }
    }
    const orders = featured.map((p) => p.order);
    const duplicates = orders.filter((o, i) => orders.indexOf(o) !== i);
    if (duplicates.length) {
      showAlert(
        `Duplicate featured order found (e.g. ${duplicates[0]}). Each product needs a unique order.`,
        "error"
      );
      return;
    }

    setSaving(true);
    try {
      await api.put("/api/admin/products/featured", { products: featured });
      clearCache(PRODUCTS_CACHE_KEY);
      loadAllFeatured();
      showAlert("Featured products updated successfully!", "success");
    } catch (err) {
      showAlert(
        err?.response?.data?.message || err?.message || "Failed to save featured products.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  /* ── Skeleton rows ── */
  const SkeletonRows = () =>
    [...Array(6)].map((_, i) => (
      <TableRow key={i}>
        <TableCell colSpan={6}>
          <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
        </TableCell>
      </TableRow>
    ));

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1500, mx: "auto" }}>

        {/* ── HEADER ── */}
        <Fade in timeout={400}>
          <Card
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              bgcolor: "background.paper",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack spacing={3}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={2}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      background: BRAND_GRAD,
                      borderRadius: 2,
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <TrendingUpIcon sx={{ color: "white", fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ background: BRAND_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                    >
                      Featured Products
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      Curate your home screen showcase — prioritise the products you want to spotlight
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Tooltip title="Featured products appear prominently on your store's home page">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={saveFeatured}
                    disabled={saving}
                    sx={{
                      background: BRAND_GRAD,
                      boxShadow: "0 4px 14px rgba(102,126,234,0.45)",
                      px: 3,
                      py: 1.25,
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { background: "linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)" },
                    }}
                  >
                    {saving ? "Saving…" : "Save Changes"}
                  </Button>
                </Stack>
              </Stack>

              <Divider />

              {/* Progress */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Featured products selected
                  </Typography>
                  <Chip
                    label={`${featuredCount} / ${MAX_FEATURED}`}
                    size="small"
                    sx={{
                      background: progressPercentage > 90
                        ? "linear-gradient(135deg,#f093fb,#f5576c)"
                        : BRAND_GRAD,
                      color: "white",
                      fontWeight: 700,
                    }}
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(progressPercentage, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 5,
                    bgcolor: "action.hover",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 5,
                      background: progressPercentage > 90
                        ? "linear-gradient(90deg,#f093fb,#f5576c)"
                        : BRAND_GRAD,
                    },
                  }}
                />
              </Box>
            </Stack>
          </Card>
        </Fade>

        {/* ── TWO-PANEL LAYOUT ── */}
        <Grid container spacing={3}>

          {/* LEFT — catalog browser */}
          <Grid item xs={12} lg={7}>
            <Stack spacing={2}>

              {/* Search */}
              <Card elevation={0} sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by product name or category…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Card>

              {/* Bulk action bar */}
              {selectedIds.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: "action.selected",
                    border: "1.5px solid",
                    borderColor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Chip
                    label={`${selectedIds.length} selected`}
                    size="small"
                    sx={{ background: BRAND_GRAD, color: "white", fontWeight: 700 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={bulkFeatureAll}
                    sx={{ background: BRAND_GRAD, textTransform: "none", fontWeight: 600, borderRadius: 1.5, boxShadow: "none" }}
                  >
                    Feature All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={bulkUnfeatureAll}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                  >
                    Unfeature All
                  </Button>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      type="number"
                      label="From order #"
                      value={bulkFromOrder}
                      onChange={(e) => setBulkFromOrder(e.target.value)}
                      inputProps={{ min: 1, max: MAX_FEATURED }}
                      sx={{ width: 120, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={bulkAssignOrders}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                    >
                      Assign Orders
                    </Button>
                  </Stack>
                  <Box sx={{ ml: "auto" }}>
                    <Button
                      size="small"
                      onClick={() => setSelectedIds([])}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Product table */}
              <Paper elevation={0} sx={{ overflow: "hidden", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell padding="checkbox" width={44}>
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={someVisibleSelected}
                          onChange={handleHeaderCheckbox}
                          disabled={loading || paginated.length === 0}
                        />
                      </TableCell>
                      <TableCell width={56} />
                      <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                      <TableCell width={90} sx={{ fontWeight: 700 }}>Featured</TableCell>
                      <TableCell width={120} sx={{ fontWeight: 700 }}>Priority Order</TableCell>
                      <TableCell width={160} sx={{ fontWeight: 700 }}>Category</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <SkeletonRows />
                    ) : paginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 6 }}>
                          <Stack alignItems="center" spacing={1.5}>
                            <SearchIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                            <Typography variant="body2" color="text.secondary">
                              No products found
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              Try adjusting your search criteria
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginated.map((p) => {
                        const imgSrc = Array.isArray(p.images) && p.images.length > 0
                          ? p.images[0]
                          : p.imageUrl || null;

                        return (
                          <TableRow
                            key={p._id}
                            hover
                            selected={selectedIds.includes(p._id)}
                            sx={{
                              borderLeft: p.featured ? "3px solid" : "3px solid transparent",
                              borderLeftColor: p.featured ? "primary.main" : "transparent",
                              transition: "background 0.15s ease",
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={selectedIds.includes(p._id)}
                                onChange={() => handleRowCheckbox(p._id)}
                              />
                            </TableCell>

                            <TableCell sx={{ py: 1 }}>
                              <ProductThumb src={imgSrc} title={p.title} size={40} />
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 220 }}>
                                {p.title}
                              </Typography>
                              {p.price != null && (
                                <Typography variant="caption" color="text.secondary">
                                  KES {Number(p.price).toLocaleString()}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              <Checkbox
                                size="small"
                                checked={!!p.featured}
                                disabled={!p.featured && featuredCount >= MAX_FEATURED}
                                onChange={(e) => toggleFeatured(p._id, e.target.checked)}
                                icon={<StarBorderIcon />}
                                checkedIcon={<StarIcon sx={{ color: "#fbbf24" }} />}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                disabled={!p.featured}
                                value={p.featuredOrder || ""}
                                onChange={(e) => setOrder(p._id, Number(e.target.value))}
                                inputProps={{ min: 1, max: MAX_FEATURED }}
                                placeholder="Order"
                                sx={{ width: 90, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                              />
                            </TableCell>

                            <TableCell>
                              {p.category?.name ? (
                                <Chip
                                  label={p.category.name}
                                  size="small"
                                  sx={{
                                    bgcolor: "action.selected",
                                    fontWeight: 500,
                                    borderRadius: 1.5,
                                  }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {meta
                      ? `${(meta.page - 1) * PAGE_SIZE + 1}–${Math.min(
                          (meta.page - 1) * PAGE_SIZE + pageProducts.length,
                          meta.total
                        )} of ${meta.total}`
                      : `${pageProducts.length} products`}
                  </Typography>
                  <Pagination
                    size="small"
                    page={page}
                    count={totalPages}
                    onChange={(_, p) => setPage(p)}
                    shape="rounded"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        background: BRAND_GRAD,
                        color: "white",
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Stack>
          </Grid>

          {/* RIGHT — current featured list */}
          <Grid item xs={12} lg={5}>
            <Card
              elevation={0}
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                position: { lg: "sticky" },
                top: { lg: 24 },
              }}
            >
              <Box sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Featured List
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sorted by priority order
                    </Typography>
                  </Box>
                  <Chip
                    label={`${featuredCount} selected`}
                    size="small"
                    sx={{ background: BRAND_GRAD, color: "white", fontWeight: 700 }}
                  />
                </Stack>
              </Box>

              {allFeaturedProducts.length === 0 ? (
                <Box sx={{ py: 8, textAlign: "center", px: 3 }}>
                  <StarBorderIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">No featured products yet</Typography>
                  <Typography variant="caption" color="text.disabled">Click the star icon in the table to feature a product</Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 500, overflowY: "auto" }}>
                  {(featuredListOverride ?? allFeaturedProducts).map(({ id, product, sel }, index) => {
                    const imgSrc = Array.isArray(product.images) && product.images.length > 0
                      ? product.images[0] : product.imageUrl || null;
                    const isOver = dragOverFeatId === id;

                    return (
                      <Box
                        key={id}
                        draggable
                        onDragStart={(e) => {
                          draggedFeatIdRef.current = id;
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverFeatId(id); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromId = draggedFeatIdRef.current;
                          if (!fromId || fromId === id) { setDragOverFeatId(null); return; }
                          const list = [...(featuredListOverride ?? allFeaturedProducts)];
                          const fromIdx = list.findIndex((x) => x.id === fromId);
                          const toIdx = list.findIndex((x) => x.id === id);
                          if (fromIdx === -1 || toIdx === -1) { setDragOverFeatId(null); return; }
                          const [moved] = list.splice(fromIdx, 1);
                          list.splice(toIdx, 0, moved);
                          setFeaturedListOverride(list);
                          setFeatOrderDirty(true);
                          draggedFeatIdRef.current = null;
                          setDragOverFeatId(null);
                        }}
                        onDragEnd={() => { draggedFeatIdRef.current = null; setDragOverFeatId(null); }}
                        sx={{
                          px: 2, py: 1.5,
                          display: "flex", alignItems: "center", gap: 1.5,
                          borderBottom: "1px solid", borderColor: "divider",
                          "&:last-child": { borderBottom: 0 },
                          cursor: "grab",
                          userSelect: "none",
                          bgcolor: isOver ? "action.selected" : "transparent",
                          outline: isOver ? "2px solid" : "none",
                          outlineColor: "primary.main",
                          transition: "background 0.12s",
                          "&:hover": { bgcolor: isOver ? "action.selected" : "action.hover" },
                        }}
                      >
                        <DragIndicatorIcon sx={{ color: "text.disabled", fontSize: 18, cursor: "grab" }} />
                        <Typography variant="caption" fontWeight={700} color="text.disabled" sx={{ minWidth: 20, textAlign: "center" }}>
                          {index + 1}
                        </Typography>
                        <ProductThumb src={imgSrc} title={product.title} size={44} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{product.title}</Typography>
                          {product.price != null && (
                            <Typography variant="caption" color="text.secondary">
                              KES {Number(product.price).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title="Remove from featured">
                          <IconButton size="small" onClick={() => removeFromFeatured(id)} sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}>
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {allFeaturedProducts.length > 0 && (
                <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<SaveIcon />}
                    onClick={saveFeatured}
                    disabled={saving}
                    sx={{
                      background: BRAND_GRAD,
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                  >
                    {saving ? "Saving…" : "Save Featured Products"}
                  </Button>
                  {featOrderDirty && (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<DragIndicatorIcon />}
                      onClick={saveFeaturedOrder}
                      disabled={savingFeatOrder}
                      sx={{ fontWeight: 700, textTransform: "none", borderRadius: 2, mt: 1 }}
                    >
                      {savingFeatOrder ? "Saving order…" : "Save Display Order"}
                    </Button>
                  )}
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
